import { DANGLING_RUNS_RETENTION_DAYS } from '@flakiness/server/common/dataRetention.js';
import type { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlInput } from '@shoelace-style/shoelace';
import { live } from 'lit/directives/live.js';
import { api } from './api.js';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts, FKProjectChanged } from './contexts.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-project-usage')
export class PageProjectUsage extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'usage'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/usage',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-usage
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-usage>
      `),
    }];
  }

  private _metrics = new Task(this, {
    args: () => [this._project] as const,
    task: async ([project], { signal }) => {
      CHECK(project);
      return await api.project.metrics.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        timeZoneId: project.timezone,
      }, { signal });
    },
  });

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;
  
  @query('#data-retention') private _dataRetention?: SlInput;
  @query('#attachments-retention') private _attachmentsRetention?: SlInput;

  private _maxDataRetentionTask = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      const result = await api.project.maxDataRetention.GET({ orgSlug, projectSlug }, { signal });
      return result ?? Infinity;
    }
  });

  private _dataRetentionTask = new Task(this, {
    args: () => [this._project, this._maxDataRetentionTask.value] as const,
    task: async ([project, maxDataRetentionDays], { signal }) => {
      CHECK(project);
      return Math.min(project.preferredDataRetentionDays ?? Infinity, maxDataRetentionDays ?? Infinity);
    }
  });

  private async _setDataRetention() {
    if (!this.orgSlug || !this.projectSlug || !this._dataRetention || !this._attachmentsRetention)
      return;
    await api.project.setProjectPreferredDataRetention.POST({
      orgSlug: this.orgSlug,
      projectSlug: this.projectSlug,
      preferredDataRetentionDays: this._dataRetention.value ? parseInt(this._dataRetention.value, 10) : undefined,
      preferredAttachmentsRetentionDays: this._attachmentsRetention.value ? parseInt(this._attachmentsRetention.value, 10) : undefined,
    });
    this.dispatchEvent(new FKProjectChanged({ orgSlug: this.orgSlug, projectSlug: this.projectSlug }));
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'Usage',
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;
    const maxDataRetentionDays = this._maxDataRetentionTask.value ?? Infinity;
    const dataRetentionDays = this._dataRetentionTask.value ?? Infinity;
    const attachmentsRetentionDays = this._project.preferredAttachmentsRetentionDays ? Math.min(this._project.preferredAttachmentsRetentionDays, dataRetentionDays) : Infinity;

    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"usage"}
      ></project-header>
      <app-body medium user-role=${this._project.access}>
        <usage-totals .metrics=${this._metrics.value}></usage-totals>

        <form-section label="Data Retention" is-editor>
          <div slot=description>
            All test results and attachments are stored for this period.
            Runs of commits that no branch or pull request points at, such as commits replaced by a force-push,
            are deleted ${DANGLING_RUNS_RETENTION_DAYS} days after upload.
            Maximum allowed by the <strong>"${this._project.org.orgSlug}"</strong> organization plan is
            <strong>${maxDataRetentionDays === Infinity ? 'unlimited' : maxDataRetentionDays + ' days'}</strong>.
          </div>
          <h-box style="align-items: start;">
            <sl-input 
              placeholder="unlimited"
              id=data-retention
              type=number
              max=${9999}
              min=${1}
              .value=${live(dataRetentionDays === Infinity ? undefined : dataRetentionDays)}
            >
              <div slot=suffix>days</div>
            </sl-input>
            <sl-button @click=${this._setDataRetention}>Save</sl-button>
          </h-box>
        </form-section>

        <form-section label="Attachments Retention" is-editor>
          <div slot=description>
            Attachments (videos, images, traces) can be stored for a shorter period than the rest of the data to save space.
            When empty, attachments follow the data retention period.
          </div>
          <h-box style="align-items: start;">
            <sl-input
              placeholder="same as data"
              id=attachments-retention
              type=number
              max=${dataRetentionDays}
              min=${1}
              .value=${live(attachmentsRetentionDays === Infinity ? undefined : attachmentsRetentionDays)}
            >
              <div slot=suffix>days</div>
            </sl-input>
            <sl-button @click=${this._setDataRetention}>Save</sl-button>
          </h-box>
        </form-section>

        <usage-metrics
          .timezone=${this._project.timezone}
          .metrics=${this._metrics.value}
          .days=${Math.min(dataRetentionDays, 365)}
        ></usage-metrics>
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`
    sl-input {
      width: 16em;
    }

    [user-role=viewer] {
      *:is([is-editor]) {
        display: none;
      }
    }
  `];
}
